import {
  Box,
  Card,
  Grid,
  Stack,
  TextField,
  Typography,
  IconButton,
  Icon,
  FormControl,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from "@mui/material";
import MDButton from "components/MDButton";
import CommonLayout from "layouts/DashboardLayout";
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useMemo, useRef, useState } from "react";
import AngleRightIcon from "common/customIcons/angle_right";
import InfoIcon from "@mui/icons-material/Info";
import { PromotionRule } from "./PromotionRule";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation } from "react-router-dom";
import { isNumber, generatePromoCodes } from "utilities/common";
import { useDispatch } from "react-redux";
import { openSnackbar } from "../../redux/slices/common";
import { promoValidation } from "validations/promo";
import { createPromo, getPromotionData, updatePromo } from "../../redux/slices/promotions";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { createContext } from "react";
import Loader from "common/Loader";

dayjs.extend(customParseFormat);

export const PlatformContext = createContext(null);

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 0, 0);
  return x;
}

function isSameCalendarDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Parse stored value (ISO string, Date, or legacy local string) for pickers. */
function parseFormDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d;
  const parsed = dayjs(value, "YYYY-MM-DD HH:mm:ss", true);
  if (parsed.isValid()) return parsed.toDate();
  return null;
}

/**
 * Persist as RFC 3339 with local offset (e.g. 2026-04-25T17:30:00+05:30).
 * Same instant as toISOString(), but wall clock matches what the user picked (not forced to ...Z UTC).
 */
function toStoredOffsetString(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  return dayjs(date).format("YYYY-MM-DDTHH:mm:ssZ");
}

function nowAsOffsetString() {
  return dayjs().format("YYYY-MM-DDTHH:mm:ssZ");
}

/** Apply clock from `timeSource` onto the calendar day of `dayAnchor` (react-datepicker needs min/max time on the selected day). */
function copyTimeOntoDay(dayAnchor, timeSource) {
  const d = new Date(dayAnchor);
  d.setHours(
    timeSource.getHours(),
    timeSource.getMinutes(),
    timeSource.getSeconds(),
    timeSource.getMilliseconds()
  );
  return d;
}

function toIsoPayload(value) {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return dayjs(d).format("YYYY-MM-DDTHH:mm:ssZ");
}

const AddPromotions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { promoId } = useParams();
  const [loading, setLoading] = useState(!!promoId);

  {
    /*----------------------------------------------------------
    --------------------------------------------------------------
      ------------------------- Variables -----------------------
      --------------------------------------------------------------
   --------------------------------------------------------------*/
  }

  const [memberShip, setMemberShip] = useState({
    promoName: "",
    status: true,
    promoDescription: "",
    discountType: "",
    discountAmount: "",
    membershipGroupType: "",
  });
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({
    ruleType: "",
    ifRules: [],
    thenRules: [],
  });

  const initialData = {
    platform: "",
    name: "",
    code: [],
    description: "",
    publishDate: "",
    expiryDate: "",
    usageLimit: "",
    usageLimitPerUser: "",
    status: "active",
    allowSalesItem: false,
    isIndividualUsed: false,
    rules: {},
    visible_status: "",
    isExpiryDateRequired: false,
    isScheduled: false,
    autoApply: false,
    priority: 1,
    stackable: true,
  };
  const selectedPlatform = location.state?.platform;
  const [formData, setFormData] = useState({ ...initialData, platform: selectedPlatform });
  const [publishDateChecked, setPublishDateChecked] = useState(false);
  const [publishDate, setPublishDate] = useState(null);
  const [expiryDateChecked, setExpiryDateChecked] = useState(false);
  const [expiryDate, setExpiryDate] = useState(null);
  const [copyStatus, setCopyStatus] = useState("");
  const [codeGenerationCount, setCodeGenerationCount] = useState("");
  const [hasManuallyEditedPromoCode, setHasManuallyEditedPromoCode] = useState(false);
  const promoCountDebounceRef = useRef(null);

  /**
   * Build payload for promotion backend (legacy create/update).
   * Backend expects: name, description, code (array), platform, status, publishDate, expiryDate,
   * isExpiryDateRequired, usageLimit, usageLimitPerUser, isScheduled, allowSalesItem, isIndividualUsed,
   * autoApply, rules { ruleType, ifRules, thenRules }, priority, stackable.
   */
  const buildPromotionPayload = (form) => {
    const code = Array.isArray(form.code)
      ? form.code.map((c) => (typeof c === "string" ? c.trim() : String(c))).filter(Boolean)
      : form.code ? [String(form.code).trim()] : [];
    const publishDate =
      toIsoPayload(form.publishDate) ?? nowAsOffsetString(); // backend requires publishDate
    const expiryDate = form.isExpiryDateRequired ? toIsoPayload(form.expiryDate) : null;
    // Backend rule types: cart, product, user, BOGO (pass through so payload matches selection)
    const backendRuleTypes = ["cart", "product", "user", "BOGO"];
    const rawRuleType = form.rules?.ruleType || "cart";
    const ruleType = backendRuleTypes.includes(rawRuleType) ? rawRuleType : "cart";
    const rules = form.rules && typeof form.rules === "object"
      ? {
        ruleType,
        ifRules: Array.isArray(form.rules.ifRules) ? form.rules.ifRules : [],
        thenRules: Array.isArray(form.rules.thenRules) ? form.rules.thenRules : [],
        ...(form.rules.creteria ? { creteria: form.rules.creteria } : {}),
      }
      : {};
    const timezone =
      typeof Intl !== "undefined" && Intl.DateTimeFormat?.().resolvedOptions?.().timeZone
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined;
    return {
      ...form,
      code,
      publishDate,
      expiryDate: expiryDate ?? undefined,
      usageLimit: form.usageLimit === "" || form.usageLimit == null ? null : Number(form.usageLimit),
      usageLimitPerUser: form.usageLimitPerUser === "" || form.usageLimitPerUser == null ? null : Number(form.usageLimitPerUser),
      rules,
      priority: form.priority != null ? Number(form.priority) : 1,
      stackable: form.stackable !== false,
      ...(timezone ? { timezone } : {}),
    };
  };

  const autoGenerateCode = () => {
    const currentCodes = [];
    const newCodes = generatePromoCodes(codeGenerationCount);
    const allCodes = [...currentCodes, ...newCodes];
    setFormData({ ...formData, code: allCodes });
    setErrors((prev) => ({ ...prev, code: "" }));
    setHasManuallyEditedPromoCode(false);
  };

  const showResponseMessage = (message, errortype) => {
    if (errortype === "success") {
      dispatch(openSnackbar({ content: message, color: "success", icon: "check" }));
    }
    if (errortype === "error") {
      dispatch(openSnackbar({ content: message, color: "error", icon: "warning" }));
    }
  };

  const handleSubmit = () => {
    const { errors, isValid } = promoValidation(formData);

    if (!isValid) {
      setErrors(errors);
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        setTimeout(() => {
          const element = document.querySelector(`[name="${firstErrorField}"]`);
          if (firstErrorField === "stateName" || firstErrorField === "city") {
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
          if (element) {
            const yOffset = -120;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: "smooth" });
            element.focus({ preventScroll: true });
          } else {
            console.warn(`Element with name="${firstErrorField}" not found`);
          }
        }, 100);
      }

      return;
    }
    // Normalize payload for promotion backend (see promotion/controllers/admin/promotionController createPromotion)
    const payload = buildPromotionPayload(formData);
    if (promoId) {
      const data = { ...payload };
      dispatch(updatePromo({ data, promoId }))
        .unwrap()
        .then((res) => {
          navigate(-1);
          showResponseMessage(res?.message, "success");
        })
        .catch((err) => {
          showResponseMessage(err?.message, "error");
        });

      return;
    }
    dispatch(createPromo(payload))
      .unwrap()
      .then((res) => {
        navigate(-1);
        showResponseMessage(res?.message, "success");
      })
      .catch((err) => {
        showResponseMessage(err?.message, "error");
      });
  };

  const handleCopyPromoCode = () => {
    if (!formData?.code?.length) {
      showResponseMessage("No promo code to copy", "error");
      return;
    }

    const textToCopy = formData.code.join(",");

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopyStatus("Copied!");
        setTimeout(() => setCopyStatus(""), 1000);
      })
      .catch(() => {
        setCopyStatus("Failed to copy");
        setTimeout(() => setCopyStatus(""), 1000);
      });
  };

  // For normal text fields (name, description, etc.)
  const normalizeSpaces = (value = "") => {
    return value
      .replace(/^\s+/, "") // no leading spaces
      .replace(/\s{2,}/g, " "); // no multiple spaces
  };

  // Final cleanup before save
  const finalizeSpaces = (value = "") => value.trim().replace(/\s{2,}/g, " ");

  const normalizePromoCodes = (value = "") => {
    return value
      .replace(/\s+/g, "") // remove ALL spaces
      .replace(/,+/g, ",") // no multiple commas
      .replace(/^,/, ""); // no leading comma
  };
  {
    /* --------------------------------------------------------------
     --------------------------------------------------------------
      ------------------------- UseEffects -----------------------
       --------------------------------------------------------------
  -------------------------------------------------------------- */
  }

  useEffect(() => {
    return () => {
      if (promoCountDebounceRef.current) {
        clearTimeout(promoCountDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!publishDateChecked && !formData.publishDate) {
      setFormData((prev) => ({
        ...prev,
        isScheduled: false,
        publishDate: nowAsOffsetString(),
      }));
    }
  }, []);

  useEffect(() => {
    if (!promoId) return;
    setLoading(true);
    dispatch(getPromotionData(promoId))
      .unwrap()
      .then((res) => {
        const promo = res?.data;

        const pubIso = toIsoPayload(promo.publishDate);
        const expIso = toIsoPayload(promo.expiryDate);
        setFormData({
          ...initialData,
          ...promo,
          publishDate: pubIso ?? promo.publishDate ?? "",
          expiryDate: expIso ?? promo.expiryDate ?? "",
          rules: {
            ...promo?.rules,
            ...(promo?.rules?.creteria ? { creteria: promo.rules.creteria } : {}),
          },
        });
        setLoading(false);
        setPublishDateChecked(Boolean(promo?.isScheduled));
        setExpiryDateChecked(Boolean(promo?.isExpiryDateRequired));
      })
      .catch((err) => {
        setLoading(false);
        showResponseMessage(err?.message || "Failed to load promotion", "error");
      });
  }, [promoId, dispatch]);

  const publishPickerTimes = useMemo(() => {
    const selected = parseFormDate(formData.publishDate);
    const now = new Date();
    if (!selected) {
      return { minTime: copyTimeOntoDay(now, now), maxTime: endOfDay(now) };
    }
    if (isSameCalendarDay(selected, now)) {
      return {
        minTime: copyTimeOntoDay(selected, now),
        maxTime: endOfDay(selected),
      };
    }
    return { minTime: startOfDay(selected), maxTime: endOfDay(selected) };
  }, [formData.publishDate]);

  const minExpiryDate = useMemo(() => {
    const todayStart = startOfDay(new Date());
    const pub = parseFormDate(formData.publishDate);
    if (!pub) return todayStart;
    const pubStart = startOfDay(pub);
    return pubStart > todayStart ? pubStart : todayStart;
  }, [formData.publishDate]);

  const expiryPickerTimes = useMemo(() => {
    const selected = parseFormDate(formData.expiryDate);
    const now = new Date();
    const pub = parseFormDate(formData.publishDate);
    if (!selected) {
      return { minTime: copyTimeOntoDay(now, now), maxTime: endOfDay(now) };
    }
    if (pub && isSameCalendarDay(selected, pub)) {
      const minT = new Date(pub.getTime());
      minT.setMinutes(minT.getMinutes() + 1);
      const maxT = endOfDay(selected);
      const minOnDay = copyTimeOntoDay(selected, minT);
      if (minOnDay >= maxT) {
        return { minTime: startOfDay(selected), maxTime: maxT };
      }
      return { minTime: minOnDay, maxTime: maxT };
    }
    if (isSameCalendarDay(selected, now)) {
      return {
        minTime: copyTimeOntoDay(selected, now),
        maxTime: endOfDay(selected),
      };
    }
    return { minTime: startOfDay(selected), maxTime: endOfDay(selected) };
  }, [formData.expiryDate, formData.publishDate]);

  if (loading) {
    return (
      <CommonLayout>
        <Loader />
      </CommonLayout>
    )
  }


  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} className="add-promotion-container">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              {/* {promoId ? "Update Promotion" : "Create New Promotion"} */}
              Promotion Dashboard
            </Typography>
          </Box>

          <Box display="flex" alignItems="center">
            <Typography
              variant="body2"
              fontWeight="600"
              color="#2E2E3A"
              style={{
                padding: "6px 20px 6px 16px",
                borderRadius: "26px",
                border: "1px solid #FFDB1E",
                background: "#FDF7D0",
              }}
            >
              {" "}
              {formData.platform
                ? formData.platform.charAt(0).toUpperCase() + formData.platform.slice(1)
                : ""}
            </Typography>
          </Box>
        </Stack>

        <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: "none" }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            gap={2}
            mb={4.5}
          >
            <Typography variant="h4" fontWeight="semibold" color="secondary">
              {/* Add Promotion */}
              {promoId ? "Update Promotion" : "Add Promotion"}
            </Typography>
            <Box display="flex" alignItems="center" gap={1.25}>
              <Typography variant="h5" fontWeight="semibold" color="secondary">
                Status
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                gap={0.75}
                lineHeight={1}
                p={0.75}
                borderRadius={2}
                bgcolor="#F2F3F7"
              >
                <MDButton
                  sx={{
                    minWidth: "92px",
                    borderRadius: 1.75,
                    minHeight: "28px",
                    p: 0.25,
                    fontWeight: 500,
                  }}
                  type="button"
                  color={formData?.status === "active" ? "success" : "dark"}
                  variant={formData?.status === "active" ? "contained" : "text"}
                  size="small"
                  onClick={() => setFormData({ ...formData, status: "active" })}
                >
                  Active
                </MDButton>
                <MDButton
                  sx={{
                    minWidth: "92px",
                    borderRadius: 1.75,
                    minHeight: "28px",
                    p: 0.25,
                    fontWeight: 500,
                  }}
                  type="button"
                  variant={formData?.status === "inactive" ? "contained" : "text"}
                  color={formData?.status === "inactive" ? "error" : "dark"}
                  size="small"
                  onClick={() => setFormData({ ...formData, status: "inactive" })}
                >
                  Inactive
                </MDButton>
              </Stack>
            </Box>
          </Stack>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={4} lg={4}>
              <TextField
                className="form-input"
                fullWidth
                label="Promotion Name*"
                name="name"
                value={formData.name || ""}
                error={Boolean(errors?.name)}
                helperText={errors?.name}
                onChange={(e) => {
                  const value = normalizeSpaces(e.target.value);

                  setErrors((prev) => ({ ...prev, name: "" }));
                  setFormData({ ...formData, name: value });
                }}
                onBlur={() => {
                  setFormData((prev) => ({
                    ...prev,
                    name: finalizeSpaces(prev.name),
                  }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <TextField
                className="form-input"
                fullWidth
                label="Promo Generation Count"
                name="promotionName"
                value={codeGenerationCount}
                onChange={(e) => {
                  if (hasManuallyEditedPromoCode) return;
                  if (e.target.value && !isNumber(e.target.value)) return;
                  setCodeGenerationCount(e.target.value);
                }}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <Box position="relative">
                <TextField
                  className="form-input"
                  fullWidth
                  label="Promo Code*"
                  name="code"
                  error={Boolean(errors?.code)}
                  helperText={errors?.code}
                  value={formData.code?.join(",") || ""}
                  onChange={(e) => {
                    setErrors((prev) => ({ ...prev, code: "" }));

                    const cleanedValue = normalizePromoCodes(e.target.value);
                    const promo = cleanedValue ? cleanedValue.split(",") : [];
                    const nonEmptyPromoCodes = promo.filter(
                      (item) => String(item || "").trim() !== ""
                    );
                    const hasCodes = nonEmptyPromoCodes.length > 0;

                    setHasManuallyEditedPromoCode(hasCodes);
                    // If count is empty and user entered promo codes manually, keep count in sync.
                    if (hasCodes && !codeGenerationCount) {
                      setCodeGenerationCount(String(nonEmptyPromoCodes.length));
                    } else if (!hasCodes) {
                      setCodeGenerationCount("");
                    }

                    // Debounced sync: update promo generation count from comma-separated manual codes.
                    if (promoCountDebounceRef.current) {
                      clearTimeout(promoCountDebounceRef.current);
                    }
                    promoCountDebounceRef.current = setTimeout(() => {
                      setCodeGenerationCount(
                        nonEmptyPromoCodes.length ? String(nonEmptyPromoCodes.length) : ""
                      );
                    }, 300);

                    setFormData({ ...formData, code: promo });
                  }}
                />

                <Tooltip title={copyStatus ? copyStatus : "Copy"} arrow>
                  <span
                    style={{ position: "absolute", right: "12px", top: "8px", cursor: "pointer" }}
                    onClick={handleCopyPromoCode}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="17"
                      height="17"
                      viewBox="0 0 17 17"
                      fill="none"
                    >
                      <path
                        d="M5.3125 2.39062C5.3125 1.65679 5.9075 1.0625 6.64062 1.0625H6.90625C7.61073 1.0625 8.28636 1.34235 8.7845 1.8405C9.28265 2.33864 9.5625 3.01427 9.5625 3.71875V5.04688C9.5625 5.78071 10.1575 6.375 10.8906 6.375H12.2188C12.9232 6.375 13.5989 6.65485 14.097 7.153C14.5951 7.65114 14.875 8.32677 14.875 9.03125V11.4219C14.875 12.155 14.28 12.75 13.5469 12.75H6.64062C6.28838 12.75 5.95057 12.6101 5.7015 12.361C5.45243 12.1119 5.3125 11.7741 5.3125 11.4219V2.39062Z"
                        fill="#2E2E3A"
                      />
                      <path
                        d="M10.625 3.71875C10.6262 2.82522 10.3045 1.96135 9.71904 1.28633C10.9043 1.59797 11.9855 2.21883 12.8521 3.08542C13.7187 3.952 14.3395 5.03321 14.6512 6.21846C13.9762 5.63301 13.1123 5.31126 12.2188 5.3125H10.8906C10.8202 5.3125 10.7526 5.28451 10.7028 5.2347C10.653 5.18488 10.625 5.11732 10.625 5.04687V3.71875ZM3.45313 4.25H4.25V11.4219C4.25 12.0559 4.50187 12.664 4.9502 13.1123C5.39853 13.5606 6.00659 13.8125 6.64063 13.8125H11.6875V14.6094C11.6875 15.3425 11.0925 15.9375 10.3594 15.9375H3.45313C3.10088 15.9375 2.76307 15.7976 2.514 15.5485C2.26493 15.2994 2.125 14.9616 2.125 14.6094V5.57812C2.125 4.84429 2.72 4.25 3.45313 4.25Z"
                        fill="#2E2E3A"
                      />
                    </svg>
                  </span>
                </Tooltip>
              </Box>
              <Typography
                variant="body2"
                fontWeight="medium"
                color="primary"
                mt={0.25}
                className="cur-pointer"
                onClick={() => {
                  if (!codeGenerationCount) {
                    return showResponseMessage(
                      "Please enter the number of promo code to generate",
                      "error"
                    );
                  }
                  if (+codeGenerationCount) {
                    autoGenerateCode();
                  }
                }}
              >
                Auto Generate Promo Code
              </Typography>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <TextField
                className="form-input"
                fullWidth
                label="Short Description"
                name="description"
                value={formData.description || ""}
                error={Boolean(errors?.description)}
                helperText={
                  errors?.description ||
                  `${String(formData.description || "").length}/100`
                }
                inputProps={{ maxLength: 100 }}
                onChange={(e) => {
                  const nextDescription = e.target.value.slice(0, 100);
                  setFormData({ ...formData, description: nextDescription });
                  if (errors?.description) {
                    setErrors((prev) => ({ ...prev, description: "" }));
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
              <hr
                style={{
                  margin: "20px 0",
                  borderColor: "#F2F3F7",
                  borderBottom: "none",
                  opacity: "0.3",
                }}
              />
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
              <Typography variant="h5" fontWeight="bold" color="#2E2E3A" mb={2.5}>
                Promotion Rules & Settings
              </Typography>
              <Typography variant="h6" fontWeight="600" color="#2E2E3A" mb={1.5}>
                Promotion Rules
              </Typography>
              {/* <MDButton
                sx={{ minWidth: '240px' }}
                type='button'
                variant='outlined'
                color='primary'
              >
                <Icon>add</Icon>&nbsp;Add Promotion Rule
              </MDButton> */}

              <PlatformContext.Provider value={formData?.platform}>
                <PromotionRule
                  data={data}
                  setData={setData}
                  formData={formData}
                  setFormData={setFormData}
                />
              </PlatformContext.Provider>
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
              <hr
                style={{
                  margin: "20px 0",
                  borderColor: "#F2F3F7",
                  borderBottom: "none",
                  opacity: "0.3",
                }}
              />
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
              <FormControl style={{ marginBottom: "5px" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(formData.autoApply || formData.rules.autoApply)}
                      onChange={(e) => setFormData({ ...formData, autoApply: e.target.checked })}
                    />
                  }
                  label="Auto Apply"
                  style={{ margin: "0" }}
                />
              </FormControl>

              <FormControl
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "40px",
                  flexDirection: "row",
                  marginBottom: "5px",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!publishDateChecked}
                      onChange={(e) => {
                        const isImmediate = e.target.checked;
                        setPublishDateChecked(!isImmediate);
                        // setFormData({ ...formData, isScheduled: !isImmediate,publishDate:new Date(), });
                        setFormData((prev) => ({
                          ...prev,
                          isScheduled: !isImmediate,
                          publishDate: isImmediate ? nowAsOffsetString() : prev.publishDate,

                          // dayjs().format("YYYY-MM-DD HH:mm:ss")
                        }));
                      }}
                    />
                  }
                  label="Publish Immediately"
                  style={{ margin: "0" }}
                />
                <Typography variant="body2" fontWeight="400" color="#9A9AAF">
                  or
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(publishDateChecked)}
                      onChange={(e) => {
                        setPublishDateChecked(e.target.checked);
                        setFormData({ ...formData, isScheduled: e.target.checked });
                      }}
                    />
                  }
                  label="Schedule"
                  style={{ margin: "0" }}
                />
              </FormControl>
              <Grid container spacing={2.5} className="promo-date-picker">
                {publishDateChecked && (
                  <Grid item xs={12} md={6}>
                    <Box display="flex" flexDirection="column" gap={2} style={{ width: "100%" }}>
                      <Typography variant="body2" fontWeight="600" color="#2E2E3A">
                        Set publish date and time
                        <Tooltip arrow title={"Choose the date and time when this promotion should go live."}>
                          <span style={{ position: "relative", left: "2px" }}>
                            <InfoIcon
                              style={{ width: "18px", height: "18px", color: "#989DA9" }}
                              className="cur-pointer"
                            />
                          </span>
                        </Tooltip>
                      </Typography>
                      <Box sx={{ width: { xs: "100%", sm: 280 }, maxWidth: 280 }}>
                        <DatePicker
                          selected={parseFormDate(formData.publishDate)}
                          minDate={startOfDay(new Date())}
                          showTimeSelect
                          timeIntervals={15}
                          timeCaption="Time"
                          dateFormat="MMMM d, yyyy h:mm aa"
                          minTime={publishPickerTimes.minTime}
                          maxTime={publishPickerTimes.maxTime}
                          onChange={(date) => {
                            if (!date) return;
                            setFormData((prev) => ({
                              ...prev,
                              publishDate: toStoredOffsetString(date),
                            }));
                          }}
                        />
                      </Box>

                      {/* <DatePicker
                      selected={formData.publishDate
                        ? dayjs(formData.publishDate, "YYYY-MM-DD HH:mm:ss").toDate()
                        : null
                      }
                      minDate={new Date()}
                      onChange={(date) => {
                        if (!date) return;
                        setFormData({
                          ...formData,
                          publishDate: dayjs(date).format("YYYY-MM-DD HH:mm:ss")
                        });
                      }}
                      label="Publish Date"
                      renderInput={(params) => <TextField {...params} size="small" />}
                    /> */}
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <Box
                    display="flex"
                    alignItems="center"
                    flexDirection="column"
                    gap={2}
                    style={{ width: "100%" }}
                  >
                    <Box display="flex" alignItems="center" style={{ width: "100%" }}>
                      <FormControl>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(expiryDateChecked)}
                              onChange={(e) => {
                                setExpiryDateChecked(e.target.checked);
                                setFormData({
                                  ...formData,
                                  isExpiryDateRequired: e.target.checked,
                                  expiryDate: "",
                                });
                              }}
                            />
                          }
                          label="Set Expiry Date"
                          style={{ margin: "0" }}
                        />
                      </FormControl>
                      <Tooltip
                        arrow
                        title={
                          "This will automatically stop being available to customers at this date and time. Expiry can be changed after the promotion is created."
                        }
                      >
                        <span style={{ display: "flex", position: "relative", left: "2px" }}>
                          <InfoIcon
                            style={{ width: "18px", height: "18px", color: "#989DA9" }}
                            className="cur-pointer"
                          />
                        </span>
                      </Tooltip>
                    </Box>
                    {expiryDateChecked && (
                      <Box display="flex" style={{ width: "100%" }}>
                        <Box sx={{ width: { xs: "100%", sm: 280 }, maxWidth: 280 }}>
                          <DatePicker
                            selected={parseFormDate(formData.expiryDate)}
                            minDate={minExpiryDate}
                            showTimeSelect
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat="MMMM d, yyyy h:mm aa"
                            minTime={expiryPickerTimes.minTime}
                            maxTime={expiryPickerTimes.maxTime}
                            onChange={(date) => {
                              if (!date) return;
                              setFormData((prev) => ({
                                ...prev,
                                expiryDate: toStoredOffsetString(date),
                              }));
                            }}
                          />
                        </Box>
                        {/* <DatePicker
                          selected={
                            formData.expiryDate
                              ? dayjs(formData.expiryDate, "YYYY-MM-DD HH:mm:ss").toDate()
                              : null
                          }
                          minDate={new Date()}
                          onChange={(date) => {
                            if (!date) return;

                            const formatted = dayjs(date).format("YYYY-MM-DD HH:mm:ss");

                            setFormData({
                              ...formData,
                              expiryDate: formatted,
                            });
                          }}
                          label="Expiry Date"
                          fullWidth
                          renderInput={(params) => <TextField {...params} size="small" />}
                        /> */}
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
              <hr
                style={{
                  margin: "20px 0",
                  borderColor: "#F2F3F7",
                  borderBottom: "none",
                  opacity: "0.3",
                }}
              />
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
              <Typography variant="h5" fontWeight="bold" color="#2E2E3A" mb={2.5}>
                Restrictions
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="body2" fontWeight="600" color="#2E2E3A">
                    Individual Use Only
                    {/* <Tooltip arrow title={"Tooltip text here"}>
                      <span style={{ position: "relative", left: "1px" }}>
                        <InfoIcon
                          style={{ width: "18px", height: "18px", color: "#989DA9" }}
                          className="cur-pointer"
                        />
                      </span>
                    </Tooltip> */}
                  </Typography>
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.stackable === false}
                          onChange={(e) =>
                            setFormData({ ...formData, stackable: !e.target.checked })
                          }
                        />
                      }
                      label="Check this box if the promotion cannot be used in conjunction with other promotions."
                      style={{ margin: "0" }}
                    />

                    {/* <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(formData.isIndividualUsed)}
                          onChange={(e) =>
                            setFormData({ ...formData, isIndividualUsed: e.target.checked })
                          }
                        />
                      }
                      label="Check this box if the promotion cannot be used in conjunction with other promotions."
                      style={{ margin: "0" }}
                    /> */}
                  </FormControl>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="600" color="#2E2E3A">
                    Exclude Sale Items
                    {/* <Tooltip arrow title={"Tooltip text here"}>
                      <span style={{ position: "relative", left: "1px" }}>
                        <InfoIcon
                          style={{ width: "18px", height: "18px", color: "#989DA9" }}
                          className="cur-pointer"
                        />
                      </span>
                    </Tooltip> */}
                  </Typography>
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(formData.allowSalesItem)}
                          onChange={(e) =>
                            setFormData({ ...formData, allowSalesItem: e.target.checked })
                          }
                        />
                      }
                      label="Check this box if the promotion should not apply to items on sale."
                      style={{ margin: "0" }}
                    />
                  </FormControl>
                </Box>
                <Box>
                  {/* <TextField
                    type="text"
                    name="priority"
                    label="Priority"
                    value={formData.priority ?? ""}
                    className="form-input"
                    
                    onChange={(e) => {
                      const raw = e.target.value;
                      const digitsOnly = raw.replace(/[^0-9]/g, "");
                      if (digitsOnly === "") {
                        setFormData({ ...formData, priority: "" });
                        return;
                      }
                      const num = Number(digitsOnly);
                      const bounded = Math.min(100, Math.max(1, num));
                      setFormData({ ...formData, priority: bounded });
                    }}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    helperText="1–100. Higher runs first when multiple promotions apply."
                  /> */}
                </Box>
                {/* <Box>
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.stackable !== false}
                          onChange={(e) =>
                            setFormData({ ...formData, stackable: e.target.checked })
                          }
                        />
                      }
                      label="Stackable: allow this promotion to be combined with other promotions."
                      style={{ margin: "0" }}
                    />
                  </FormControl>
                </Box> */}
              </Box>
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
              <hr
                style={{
                  margin: "20px 0",
                  borderColor: "#F2F3F7",
                  borderBottom: "none",
                  opacity: "0.3",
                }}
              />
            </Grid>

            {/* <Grid item xs={12} md={12} lg={12}>
              <Typography variant='h5' fontWeight="bold" color="#2E2E3A" mb={2.5}>
                Promotion Tags
                <Tooltip arrow title={"Tooltip text here"}>
                  <span style={{ position: "relative", left: "1px" }}>
                    <InfoIcon style={{ width: '18px', height: '18px', color: '#989DA9' }} className="cur-pointer" />
                  </span>
                </Tooltip>
              </Typography>
              <Typography variant='h6' fontWeight="600" color="#2E2E3A" mb={2.5}>Tags</Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6} lg={4}>
                  <FormControl fullWidth className='select-arrow-size'>
                    <InputLabel id="">Select Promotion Type</InputLabel>
                    <Select
                      className="form-select"
                      labelId=""
                      id=""
                      value=""
                      label="Select Promotion Type"
                      name=''
                      required
                    >
                      <MenuItem value=''>Type 1</MenuItem>
                      <MenuItem value=''>Type 2</MenuItem>
                      <MenuItem value=''>Type 3</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <FormControl fullWidth className='select-arrow-size'>
                    <InputLabel id="">Select Tag</InputLabel>
                    <Select
                      className="form-select"
                      labelId=""
                      id=""
                      value=""
                      label="Select Tag"
                      name=''
                      required
                    >
                      <MenuItem value=''>Tag 1</MenuItem>
                      <MenuItem value=''>Tag 2</MenuItem>
                      <MenuItem value=''>Tag 3</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <hr style={{ margin: "20px 0", borderColor: "#F2F3F7", borderBottom: "none", opacity: "0.3" }} />
            </Grid> */}

            <Grid item xs={12} md={12} lg={12}>
              <Typography variant="h5" fontWeight="bold" color="#2E2E3A" mb={2.5}>
                Usage Limit
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6} lg={4}>
                  <Box position="relative">
                    {/* <TextField
                      className="form-input"
                      fullWidth
                      type="number"
                      label="Usage Limit Per Promotion"
                      name="usageLimit"
                      value={formData.usageLimit ?? ""}
                      // onChange={(e) => {
                      //   setFormData({ ...formData, usageLimit: e.target.value });
                      // }}
                      onKeyDown={(e) => {
                        if (e.key === "." || e.key === "e") e.preventDefault();
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || Number.isInteger(Number(val))) {
                          setFormData({ ...formData, usageLimit: val === "" ? "" : Number(val) });
                        }
                      }}
                    /> */}

                    <TextField
                      type="text"
                      fullWidth
                      name="usageLimit"
                      className="form-input"
                      label="Total Usage Limit (all users)"
                      value={formData.usageLimit ?? ""}
                      error={Boolean(errors?.usageLimit)}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^[1-9]\d*$/.test(val)) {
                          setFormData({
                            ...formData,
                            usageLimit: val === "" ? "" : Number(val),
                          });
                        }
                      }}
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      helperText={errors?.usageLimit}
                    />
                    <Tooltip
                      arrow
                      title="Maximum number of uses across all users."
                    >
                      <span style={{ position: "absolute", right: "16px", top: "8px" }}>
                        <InfoIcon
                          style={{ width: "20px", height: "20px", color: "#989DA9" }}
                          className="cur-pointer"
                        />
                      </span>
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Box position="relative">
                    <TextField
                      type="text"
                      fullWidth
                      name="usageLimitPerUser"
                      className="form-input"
                      label="Usage Limit Per User"
                      value={formData.usageLimitPerUser ?? ""}
                      error={Boolean(errors?.usageLimitPerUser)}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^[1-9]\d*$/.test(val)) {
                          setFormData({
                            ...formData,
                            usageLimitPerUser: val === "" ? "" : Number(val),
                          });
                        }
                      }}
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      helperText={errors?.usageLimitPerUser }
                    />
                    <Tooltip
                      arrow
                      title="Maximum number of uses per user."
                    >
                      <span style={{ position: "absolute", right: "16px", top: "8px" }}>
                        <InfoIcon
                          style={{ width: "20px", height: "20px", color: "#989DA9" }}
                          className="cur-pointer"
                        />
                      </span>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} my={3}>
                <MDButton
                  sx={{ minWidth: "160px", boxShadow: "none !important" }}
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={() => handleSubmit()}
                >
                  {promoId ? "Update" : "Save"}
                </MDButton>
                {/* <MDButton
                  sx={{
                    minWidth: "160px",
                    backgroundColor: "#F1F1F1 !important",
                    color: "#0163FF !important",
                    boxShadow: "none !important",
                  }}
                  type="button"
                  variant="contained"
                  color="error"
                  onClick={() => handleSubmit("draft")}
                >
                  Save as Draft
                </MDButton> */}
                <MDButton
                  sx={{ minWidth: "160px" }}
                  type="button"
                  variant="outlined"
                  color="dark"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </MDButton>
              </Stack>
            </Grid>
          </Grid>
        </Card>
      </Box>
    </CommonLayout>
  );
};

export default AddPromotions;
